import React, { forwardRef, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { MaskitoDateMode, maskitoDateOptionsGenerator } from '@maskito/kit';
import { DatePicker } from 'antd';
import { PickerMode, PickerRef } from '@rc-component/picker/es/interface';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isAfter, isBefore } from 'date-fns';
import { dateFormat, datePickerDateFormat } from '@lib/enums/dateTime';
import { revertTransformDate, transformDate } from '@lib/utils/dateTimeHelpers';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Input from '@lib/components/Input/Input';
import { InputSizes } from '@lib/components/Input/enums';
import isMobile from '@lib/utils/isMobile';
import getDayjsLocale from '@lib/utils/dayjsLocale';
import styles from './DateTimePicker.module.scss';

// Ensure parsing with custom formats works regardless of app-level setup
dayjs.extend(customParseFormat);

function useSelectedDate({
  value,
  format,
  isFocused,
  pickerType,
  locale,
}: {
  value?: string;
  format: string;
  isFocused?: boolean;
  pickerType?: PickerMode;
  locale: string;
}) {
  const isValueExists = !!value;
  const selected = isValueExists ? dayjs(revertTransformDate(value)) : null;
  // Format with the platform language so month names (e.g. `MMM YYYY`) are localized
  const inputValue = selected ? selected.locale(locale).format(format) : '';
  const placeholder = isFocused ? format.toUpperCase() : undefined;

  // Use numeric DD/MM/YYYY mask only for the default day picker format.
  // For year/month/quarter/week pickers we should not restrict typing to numbers only.
  const isDefaultDateFormat = format === datePickerDateFormat && !pickerType;
  const mask = isDefaultDateFormat
    ? maskitoDateOptionsGenerator({
        mode: dateFormat.toLowerCase() as MaskitoDateMode,
        separator: '/',
      })
    : undefined;
  return {
    mask,
    selected,
    inputValue,
    placeholder,
    isValueExists,
    isDefaultDateFormat,
  };
}

const getDisabledDate =
  (pickerType?: PickerMode, minDate?: Date, maxDate?: Date) => (current) => {
    if (!current) return false;
    if (pickerType === 'year') {
      const startOfYear = dayjs(current).startOf('year').toDate();
      const endOfYear = dayjs(current).endOf('year').toDate();
      if (minDate && isBefore(endOfYear, minDate)) return true;
      if (maxDate && isAfter(startOfYear, maxDate)) return true;
      return false;
    }
    if (pickerType === 'month') {
      const startOfMonth = dayjs(current).startOf('month').toDate();
      const endOfMonth = dayjs(current).endOf('month').toDate();
      if (minDate && isBefore(endOfMonth, minDate)) return true;
      if (maxDate && isAfter(startOfMonth, maxDate)) return true;
      return false;
    }
    const currentDate = current.toDate();
    if (minDate && isBefore(currentDate, minDate)) return true;
    if (maxDate && isAfter(currentDate, maxDate)) return true;
    return false;
  };

function ExtraFooter({
  maxDate,
  minDate,
  onChangeHandler,
  selected,
}: {
  maxDate?: Date;
  minDate?: Date;
  onChangeHandler: (v: Dayjs | null) => void;
  selected: Dayjs | null;
}) {
  const { t } = useTranslation();
  const today = dayjs().startOf('day');
  if (
    (maxDate &&
      (today.isSame(dayjs(maxDate), 'day') ||
        today.isAfter(dayjs(maxDate), 'day'))) ||
    (minDate &&
      (today.isSame(dayjs(minDate), 'day') ||
        today.isBefore(dayjs(minDate), 'day')))
  ) {
    // Do not render footer if today is maxDate or minDate
    return null;
  }

  const isSelected = selected && selected.isSame(today, 'day');
  return (
    <div className={styles.dateRangePickerFooter}>
      <Button
        buttonText={t('today')}
        buttonSize={ButtonSizes.small}
        buttonType={
          isSelected ? ButtonTypes.primaryFilled : ButtonTypes.primaryOutlined
        }
        onClick={() => {
          onChangeHandler(today);
        }}
        useRipple={false}
      />
    </div>
  );
}

type Props = {
  allowClear?: boolean;
  disabled?: boolean;
  format?: string;
  hasError?: boolean;
  isFocused?: boolean;
  maxDate?: Date;
  minDate?: Date;
  name?: string;
  onChange: (v: string) => void;
  pickerType?: PickerMode;
  selectEndOfTheDay?: boolean;
  size?: InputSizes;
  value?: string;
};

const DatePickerCommon = forwardRef(
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<PickerRef>) => {
    const {
      allowClear: allowClearProp = true,
      disabled,
      format = datePickerDateFormat,
      hasError,
      isFocused,
      maxDate,
      minDate,
      name,
      onChange,
      pickerType,
      selectEndOfTheDay,
      setIsFocused,
      size = InputSizes.large,
      value,
    } = props;
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const [rawInput, setRawInput] = useState<string | undefined>(undefined);
    const { i18n } = useTranslation();
    const locale = getDayjsLocale(i18n.language);
    const { phone, tablet } = isMobile();
    const isMobileDevice = phone || tablet;

    const {
      mask,
      selected,
      inputValue,
      placeholder,
      isValueExists,
      isDefaultDateFormat,
    } = useSelectedDate({ value, format, isFocused, pickerType, locale });

    const shouldUseOnBlur = !(isValueExists || isDropdownOpened);
    const onBlurHandler = shouldUseOnBlur
      ? (e) => {
          const className =
            e?.relatedTarget?.className || e?.target?.className || '';
          if (className.indexOf('ant-picker') === -1) {
            setIsFocused(false);
            setDropdownOpened(false);
          }
        }
      : undefined;

    const onCloseHandler = () => {
      setIsFocused(false);
      setDropdownOpened(false);
      setRawInput(undefined);
    };

    const onFocusHandler = () => {
      setDropdownOpened(true);
      setIsFocused(true);
      if (rawInput === undefined) setRawInput(inputValue);
    };

    const onChangeHandler = useCallback(
      (v: Dayjs | null) => {
        let newValue = '';
        if (v) {
          newValue = transformDate(v.toDate(), selectEndOfTheDay);
        } else if (isMobileDevice) {
          setIsFocused(false);
        }
        onChange(newValue);
        setTimeout(() => {
          setDropdownOpened(false);
        }, 0);
        setRawInput(undefined);
      },
      [isMobileDevice, onChange, selectEndOfTheDay, setIsFocused],
    );

    const onInputChangeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const input = e.target.value;
      if (input === '') {
        setRawInput('');
        return;
      }
      setRawInput(input);
      const parsed = dayjs(input, format, locale, true);
      if (parsed.isValid()) {
        onChangeHandler(parsed);
      }
    };

    const onInputBlurHandler = () => {
      if (rawInput === '') {
        onChangeHandler(null);
        return;
      }
      // If user leaves an invalid/partial value, revert to the last committed value
      if (rawInput !== undefined && rawInput !== '') {
        const parsed = dayjs(rawInput, format, locale, true);
        if (!parsed.isValid()) {
          setRawInput(undefined);
        }
      }
    };

    const onClearHandler = () => onChangeHandler(null);

    const className = classNames(styles.picker, {
      [styles.datePickerFilled]: isValueExists,
    });

    const allowClear = allowClearProp
      ? { clearIcon: <MaterialIcon icon="cancel" /> }
      : false;

    return (
      <div className={styles.rootDatePickerCommon}>
        <div className={styles.pickerWrapper}>
          <DatePicker
            allowClear={allowClear}
            className={className}
            defaultPickerValue={selected || dayjs()}
            disabled={disabled}
            disabledDate={getDisabledDate(pickerType, minDate, maxDate)}
            format={format}
            inputReadOnly={isMobileDevice}
            name={`${name}-picker`}
            needConfirm={false}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            open={isDropdownOpened}
            picker={pickerType}
            placeholder=""
            classNames={{
              popup: {
                root: styles.datePickerPopup,
              },
            }}
            ref={ref}
            renderExtraFooter={() => (
              <ExtraFooter
                onChangeHandler={onChangeHandler}
                selected={selected}
                maxDate={maxDate}
                minDate={minDate}
              />
            )}
            showNow={false}
            showWeek={false}
            size="large"
            status={hasError ? 'error' : undefined}
            value={selected}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            disabled={disabled}
            disabledIcon="calendar_today"
            hasError={hasError}
            inputMode={isDefaultDateFormat ? 'numeric' : undefined}
            mask={mask}
            name={name}
            onBlur={onInputBlurHandler}
            onChange={onInputChangeHandler}
            onClear={allowClearProp ? onClearHandler : undefined}
            onRightIconClick={onFocusHandler}
            placeholder={placeholder}
            rightIcon="calendar_today"
            rightIconSymbolsOutlined
            size={size}
            value={rawInput !== undefined ? rawInput : inputValue}
          />
        </div>
        {isDropdownOpened && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <div className={styles.mask} onClick={onCloseHandler} />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(DatePickerCommon));
